/**
 * Landing page — cinematic hero with 3D F1 car + scroll-tied Y rotation.
 * Sections: Hero, Live Race Intelligence, Dynamic Dashboard, Designed for New Fans,
 * Explore F1, Final CTA.
 */
import React, { useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import {
    ArrowRight,
    Map,
    Gauge,
    Brain,
    Trophy,
    Users,
    CalendarDays,
    Newspaper,
    GraduationCap,
    BarChart3,
    Clock,
    ChevronDown,
    Activity,
    Radio,
} from "lucide-react";
import Hero3DCar from "../components/Hero3DCar";
import {
    LiveStatusPill,
    CountdownWidget,
    SectionHeader,
    Reveal,
} from "../components/atoms";
import { api, useFetch } from "../lib/api";

const LIVE_ROUTE = "/race/live";

export default function Landing() {
    const navigate = useNavigate();
    const openLiveDashboard = () => navigate(LIVE_ROUTE);
    const heroRef = useRef(null);
    const { scrollYProgress } = useScroll({
        target: heroRef,
        offset: ["start start", "end start"],
    });
    const heroTextY = useTransform(scrollYProgress, [0, 1], [0, -60]);
    const heroOpacity = useTransform(scrollYProgress, [0, 0.6], [1, 0]);

    const { data: nextRaceResp } = useFetch(() => api.scheduleNext(), []);
    const nextRace = nextRaceResp?.race;

    return (
        <div className="relative overflow-hidden bg-graphite-900">
            {/* HERO */}
            <section
                ref={heroRef}
                className="relative z-10 min-h-[100svh] flex flex-col overflow-hidden"
                data-testid="hero-section"
            >
                {/* Vignette + gradient overlay for text legibility */}
                <div className="absolute inset-0 pointer-events-none bg-[radial-gradient(ellipse_60%_50%_at_70%_50%,transparent_30%,rgba(10,10,12,0.75)_75%,rgba(10,10,12,0.95)_100%)]" />
                {/* Left fade to ensure text readability */}
                <div className="absolute inset-y-0 left-0 w-1/2 lg:w-3/5 bg-gradient-to-r from-graphite-900 via-graphite-900/85 to-transparent pointer-events-none" />
                <div className="absolute inset-x-0 bottom-0 h-48 bg-gradient-to-t from-graphite-900 via-graphite-900/80 to-transparent pointer-events-none" />
                <div className="absolute inset-0 grid-bg opacity-[0.25] pointer-events-none" />
                <div className="absolute inset-0 z-0 pointer-events-none lg:left-[35%]" aria-hidden="true">
                    <Hero3DCar scrollTarget={heroRef} />
                </div>

                {/* Hero content */}
                <motion.div
                    style={{ y: heroTextY, opacity: heroOpacity }}
                    className="relative z-10 flex-1 flex items-center"
                >
                    <div className="max-w-7xl mx-auto w-full px-4 sm:px-6 lg:px-8 pt-32 pb-24">
                        <div className="max-w-3xl">
                            {/* Live status */}
                            <motion.div
                                initial={{ opacity: 0, y: 14 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.6, delay: 0.05 }}
                                className="flex flex-wrap items-center gap-3 mb-7"
                            >
                                <LiveStatusPill status="replay" label="RACE INTELLIGENCE" />
                                {nextRace && (
                                    <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/5 border border-white/10">
                                        <Radio className="h-3 w-3 text-zinc-400" />
                                        <span className="font-mono text-[10px] tracking-widest text-zinc-300">
                                            NEXT&nbsp;·&nbsp;{nextRace.name?.toUpperCase()}
                                        </span>
                                    </span>
                                )}
                            </motion.div>

                            {/* Eyebrow */}
                            <motion.div
                                initial={{ opacity: 0, y: 14 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.6, delay: 0.12 }}
                                className="label-eyebrow mb-5"
                            >
                                Outlapped · Race Intelligence
                            </motion.div>

                            {/* Title */}
                            <motion.h1
                                initial={{ opacity: 0, y: 18 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.7, delay: 0.18, ease: [0.22, 1, 0.36, 1] }}
                                className="font-display text-[clamp(2.75rem,8vw,7rem)] leading-[0.92] tracking-tight"
                                data-testid="hero-title"
                            >
                                F1
                                <br />
                                <span className="bg-gradient-to-r from-f1-red via-red-500 to-orange-400 bg-clip-text text-transparent">
                                    Race Intelligence.
                                </span>
                            </motion.h1>

                            {/* Subtitle */}
                            <motion.p
                                initial={{ opacity: 0, y: 14 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.6, delay: 0.34 }}
                                className="mt-7 max-w-xl text-base sm:text-lg text-zinc-400 leading-relaxed"
                                data-testid="hero-subtitle"
                            >
                                Race strategy, historical replay, driver tracking, and Formula 1
                                events explained in plain English.
                            </motion.p>

                            {/* CTAs */}
                            <motion.div
                                initial={{ opacity: 0, y: 14 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.6, delay: 0.45 }}
                                className="mt-9 flex flex-wrap items-center gap-3"
                            >
                                <button
                                    type="button"
                                    onClick={openLiveDashboard}
                                    data-testid="cta-open-dashboard"
                                    className="group inline-flex items-center gap-2 px-6 py-3.5 bg-f1-red hover:bg-red-700 text-white rounded-md font-medium tracking-wide transition-all red-glow"
                                >
                                    Open Race Dashboard
                                    <ArrowRight className="h-4 w-4 group-hover:translate-x-0.5 transition-transform" />
                                </button>
                                <Link
                                    to="/calendar"
                                    data-testid="cta-view-calendar"
                                    className="inline-flex items-center gap-2 px-6 py-3.5 border border-white/15 hover:bg-white/5 text-white rounded-md font-medium tracking-wide transition-colors"
                                >
                                    View Race Calendar
                                </Link>
                            </motion.div>

                            {/* Countdown badge */}
                            {nextRace?.race_start && (
                                <motion.div
                                    initial={{ opacity: 0, y: 14 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ duration: 0.6, delay: 0.58 }}
                                    className="mt-12 inline-block glass-strong rounded-2xl p-5 sm:p-6"
                                >
                                    <CountdownWidget
                                        target={nextRace.race_start}
                                        label={nextRace.short_name || nextRace.name}
                                    />
                                </motion.div>
                            )}
                        </div>
                    </div>
                </motion.div>

                {/* Scroll indicator */}
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1, y: [0, 8, 0] }}
                    transition={{
                        opacity: { duration: 1, delay: 1 },
                        y: { duration: 2, repeat: Infinity, ease: "easeInOut" },
                    }}
                    className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 flex flex-col items-center gap-2 pointer-events-none"
                >
                    <span className="label-eyebrow text-zinc-500">SCROLL</span>
                    <ChevronDown className="h-4 w-4 text-zinc-500" />
                </motion.div>
            </section>

            {/* LIVE RACE INTELLIGENCE */}
            <section className="relative z-10 py-24 md:py-32" data-testid="section-intelligence">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <Reveal>
                        <SectionHeader
                            eyebrow="LIVE RACE INTELLIGENCE"
                            title="Read the race like an engineer."
                            subtitle="Three core tools that turn raw race data into something you actually understand — even if it's your first season."
                        />
                    </Reveal>
                    <div className="mt-16 grid md:grid-cols-3 gap-5">
                        {INTELLIGENCE_CARDS.map((c, i) => (
                            <Reveal key={c.title} delay={i * 0.07}>
                                <FeatureCard {...c} />
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>

            {/* DYNAMIC RACE DASHBOARD PREVIEW */}
            <section className="relative z-10 py-24 md:py-32 border-t border-white/5 overflow-hidden" data-testid="section-dashboard-preview">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="grid lg:grid-cols-12 gap-12 items-center">
                        <Reveal className="lg:col-span-5">
                            <div className="max-w-md">
                                <SectionHeader
                                    eyebrow="DYNAMIC DASHBOARD"
                                    title="The dashboard adapts to the race."
                                    subtitle="If a live provider is configured, the dashboard can surface live data. For the MVP, it focuses on countdowns, historical race replay, and post-race analysis."
                                />
                                <button
                                    type="button"
                                    onClick={openLiveDashboard}
                                    data-testid="cta-go-dashboard"
                                    className="mt-7 inline-flex items-center gap-2 text-f1-red font-medium hover:gap-3 transition-all"
                                >
                                    Open Race Dashboard <ArrowRight className="h-4 w-4" />
                                </button>
                            </div>
                        </Reveal>
                        <Reveal delay={0.1} className="lg:col-span-7">
                            <DashboardPreview />
                        </Reveal>
                    </div>
                </div>
            </section>

            {/* DESIGNED FOR NEW FANS */}
            <section className="relative z-10 py-24 md:py-32 border-t border-white/5">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <Reveal>
                        <SectionHeader
                            eyebrow="DESIGNED FOR NEW FANS"
                            title="Every event, explained in plain English."
                            subtitle="No prior F1 knowledge required. Tap any event for the why behind it."
                        />
                    </Reveal>
                    <div className="mt-16 grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
                        {FAN_CARDS.map((c, i) => (
                            <Reveal key={c.term} delay={i * 0.05}>
                                <Link
                                    to="/learn"
                                    data-testid={`fan-card-${c.term.toLowerCase().replace(/\s+/g, "-")}`}
                                    className="block p-6 rounded-xl bg-graphite-800/40 border border-white/5 hover:border-f1-red/50 transition-all duration-300 group"
                                >
                                    <div className="font-mono text-[10px] tracking-widest text-zinc-500">
                                        {c.label}
                                    </div>
                                    <div className="mt-2 font-display text-2xl tracking-wide group-hover:text-f1-red transition-colors">
                                        {c.term}
                                    </div>
                                    <div className="mt-3 text-sm text-zinc-400 leading-relaxed">
                                        {c.desc}
                                    </div>
                                </Link>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>

            {/* EXPLORE F1 */}
            <section className="relative z-10 py-24 md:py-32 border-t border-white/5">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <Reveal>
                        <SectionHeader
                            eyebrow="EXPLORE F1"
                            title="Everything Formula 1, in one place."
                        />
                    </Reveal>
                    <div className="mt-16 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                        {EXPLORE_CARDS.map((c, i) => (
                            <Reveal key={c.title} delay={i * 0.05}>
                                <ExploreCard {...c} />
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>

            {/* FINAL CTA */}
            <section className="relative z-10 py-24 md:py-32 overflow-hidden" data-testid="section-final-cta">
                <div className="absolute inset-0 bg-gradient-to-br from-f1-red via-red-900 to-graphite-900" />
                <div className="absolute inset-0 diagonal-stripes opacity-50" />
                <div className="absolute inset-0 bg-[radial-gradient(ellipse_60%_60%_at_50%_120%,rgba(0,0,0,0.6),transparent_70%)]" />
                <div className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
                    <Reveal>
                        <div className="label-eyebrow text-white/70 mb-4">READY TO RACE</div>
                        <h2 className="font-display text-5xl sm:text-6xl lg:text-7xl tracking-tight leading-[0.95]">
                            Follow every race
                            <br />
                            like an analyst.
                        </h2>
                        <button
                            type="button"
                            onClick={openLiveDashboard}
                            data-testid="cta-final-open-dashboard"
                            className="mt-10 inline-flex items-center gap-2 px-7 py-4 bg-white text-graphite-900 hover:bg-zinc-100 rounded-md font-semibold tracking-wide transition-all"
                        >
                            Open Dashboard
                            <ArrowRight className="h-4 w-4" />
                        </button>
                    </Reveal>
                </div>
            </section>
        </div>
    );
}

// ---------------------------------------------------------------------------
function FeatureCard({ icon: Icon, title, desc }) {
    return (
        <div className="relative p-7 rounded-xl bg-graphite-800/40 border border-white/5 hover:border-white/20 transition-all duration-300 group overflow-hidden">
            <div className="absolute top-0 left-0 h-px w-12 bg-f1-red group-hover:w-24 transition-all duration-500" />
            <div className="h-12 w-12 rounded-lg bg-f1-red/10 border border-f1-red/30 grid place-items-center text-f1-red">
                <Icon className="h-5 w-5" />
            </div>
            <h3 className="mt-6 font-display text-2xl tracking-wide">{title}</h3>
            <p className="mt-3 text-sm text-zinc-400 leading-relaxed">{desc}</p>
        </div>
    );
}

const INTELLIGENCE_CARDS = [
    {
        icon: Map,
        title: "Interactive Driver Map",
        desc: "Watch every driver move around the circuit in real time, colored by team and ranked by position.",
    },
    {
        icon: Gauge,
        title: "Replay Telemetry",
        desc: "Speed, throttle, brake, gear, DRS, and tire context for the race sessions available in your replay library.",
    },
    {
        icon: Brain,
        title: "Smart Event Explanations",
        desc: "Overtakes, undercuts, safety cars and pit strategy — explained in plain English with the evidence.",
    },
];

const FAN_CARDS = [
    {
        label: "FAN GUIDE",
        term: "What is DRS?",
        desc: "An adjustable rear wing flap that gives the trailing car a top-speed boost on straights.",
    },
    {
        label: "STRATEGY",
        term: "What is an Undercut?",
        desc: "Pit before your rival, use fresh tires to set fast laps, then come out ahead.",
    },
    {
        label: "AERO",
        term: "Why are tires losing grip?",
        desc: "Heat, wear, and chemical degradation reduce how much rubber sticks to the track.",
    },
    {
        label: "RACE CRAFT",
        term: "Why did that overtake happen?",
        desc: "Faster car, DRS, slipstream, and a brave braking zone — we explain each piece.",
    },
];

const EXPLORE_CARDS = [
    { icon: Users, title: "Drivers", desc: "Profiles, stats, recent form, head-to-head.", to: "/drivers" },
    { icon: Trophy, title: "Teams", desc: "Constructors, drivers, pit performance.", to: "/teams" },
    { icon: CalendarDays, title: "Calendar", desc: "Season schedule with timezone conversion.", to: "/calendar" },
    { icon: Newspaper, title: "Race IQ Briefing", desc: "F1 headlines explained with intelligence context.", to: "/news" },
    { icon: BarChart3, title: "Records", desc: "Historical records, standings, charts.", to: "/standings" },
    { icon: GraduationCap, title: "Learn F1", desc: "Every term and strategy, explained.", to: "/learn" },
];

function ExploreCard({ icon: Icon, title, desc, to }) {
    return (
        <Link
            to={to}
            data-testid={`explore-card-${title.toLowerCase().replace(/\s+/g, "-")}`}
            className="group relative block p-7 rounded-xl bg-graphite-800/40 border border-white/5 hover:border-white/20 hover:-translate-y-0.5 transition-all duration-300 overflow-hidden"
        >
            <div className="absolute inset-y-0 right-0 w-32 bg-gradient-to-l from-f1-red/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
            <div className="relative flex items-start gap-4">
                <div className="h-11 w-11 rounded-lg bg-white/5 border border-white/10 grid place-items-center text-zinc-300 group-hover:text-f1-red group-hover:border-f1-red/40 transition-colors">
                    <Icon className="h-5 w-5" />
                </div>
                <div className="flex-1">
                    <h3 className="font-display text-xl tracking-wide">{title}</h3>
                    <p className="mt-1 text-sm text-zinc-400">{desc}</p>
                </div>
                <ArrowRight className="h-4 w-4 text-zinc-600 group-hover:text-f1-red group-hover:translate-x-1 transition-all" />
            </div>
        </Link>
    );
}

// ---------------------------------------------------------------------------
function DashboardPreview() {
    const fakeDrivers = [
        { p: 1, c: "NOR", color: "#FF8000", gap: "Leader", delta: "+0" },
        { p: 2, c: "VER", color: "#3671C6", gap: "+1.842", delta: "-1" },
        { p: 3, c: "LEC", color: "#E80020", gap: "+4.301", delta: "+0" },
        { p: 4, c: "PIA", color: "#FF8000", gap: "+5.118", delta: "+1" },
        { p: 5, c: "HAM", color: "#27F4D2", gap: "+12.74", delta: "+0" },
    ];
    return (
        <div className="relative">
            <div className="relative glass-strong rounded-2xl p-5 sm:p-6 border border-white/10 overflow-hidden">
                <div className="light-sweep" />
                <div className="flex items-center justify-between gap-2 mb-5">
                    <div className="flex items-center gap-2">
                        <Activity className="h-3.5 w-3.5 text-f1-red" />
                        <span className="font-mono text-[11px] tracking-[0.22em] text-zinc-300">
                            DASHBOARD&nbsp;·&nbsp;LAP 27 / 66
                        </span>
                    </div>
                    <LiveStatusPill status="replay" label="REPLAY READY" />
                </div>

                <div className="grid grid-cols-12 gap-3">
                    {/* Race replay map mock */}
                    <div className="col-span-12 sm:col-span-7 h-56 rounded-lg bg-black/50 border border-white/5 relative overflow-hidden">
                        <div className="absolute inset-0 grid-bg opacity-30" />
                        <svg viewBox="0 0 240 150" className="absolute inset-0 w-full h-full p-5" preserveAspectRatio="xMidYMid meet">
                            <defs>
                                <filter id="landingMapGlow" x="-30%" y="-30%" width="160%" height="160%">
                                    <feGaussianBlur stdDeviation="2.5" result="blur" />
                                    <feMerge>
                                        <feMergeNode in="blur" />
                                        <feMergeNode in="SourceGraphic" />
                                    </feMerge>
                                </filter>
                            </defs>
                            <path
                                d="M 32 94 C 22 42, 75 28, 120 41 C 168 55, 203 31, 213 61 C 222 89, 176 118, 126 110 C 83 103, 48 131, 32 94 Z"
                                fill="none"
                                stroke="rgba(255,255,255,0.18)"
                                strokeWidth="13"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                            />
                            <path
                                d="M 32 94 C 22 42, 75 28, 120 41 C 168 55, 203 31, 213 61 C 222 89, 176 118, 126 110 C 83 103, 48 131, 32 94 Z"
                                fill="none"
                                stroke="rgba(225,6,0,0.58)"
                                strokeWidth="3"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                filter="url(#landingMapGlow)"
                            />
                            {[
                                { x: 120, y: 41, c: "#FF8000", label: "1 NOR" },
                                { x: 190, y: 79, c: "#3671C6", label: "2 VER" },
                                { x: 58, y: 111, c: "#E80020", label: "3 LEC" },
                            ].map((d) => (
                                <g key={d.label}>
                                    <circle cx={d.x} cy={d.y} r="4.2" fill={d.c} filter="url(#landingMapGlow)" />
                                    <rect x={d.x + 6} y={d.y - 10} width="38" height="15" rx="3" fill="rgba(8,8,10,0.86)" stroke="rgba(255,255,255,0.16)" />
                                    <text x={d.x + 12} y={d.y + 1} fill="white" fontSize="7" fontFamily="monospace" fontWeight="700">{d.label}</text>
                                </g>
                            ))}
                        </svg>
                        <div className="absolute bottom-3 left-3 font-mono text-[9px] tracking-widest text-zinc-500">
                            RACE MAP · PROJECTED POSITION
                        </div>
                        <div className="absolute right-3 top-3 rounded-full border border-f1-red/25 bg-f1-red/10 px-2.5 py-1 font-mono text-[9px] tracking-widest text-f1-red">
                            QUALITY GOOD
                        </div>
                    </div>

                    {/* Leaderboard mock */}
                    <div className="col-span-12 sm:col-span-5 rounded-lg bg-black/40 border border-white/5 p-3">
                        <div className="font-mono text-[10px] tracking-widest text-zinc-500 mb-2">LEADERBOARD</div>
                        <div className="space-y-1.5">
                            {fakeDrivers.map((d) => (
                                <div key={d.c} className="flex items-center gap-2 text-xs">
                                    <span className="w-5 font-mono text-zinc-500">P{d.p}</span>
                                    <span className="h-3 w-1 rounded-sm" style={{ background: d.color }} />
                                    <span className="font-mono font-semibold w-10">{d.c}</span>
                                    <span className="flex-1 text-zinc-400 font-mono">{d.gap}</span>
                                    <span className="font-mono text-zinc-500 text-[10px]">{d.delta}</span>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Event log mock */}
                    <div className="col-span-12 rounded-lg bg-black/40 border border-white/5 p-3">
                        <div className="flex items-center justify-between mb-2">
                            <div className="font-mono text-[10px] tracking-widest text-zinc-500">EVENT LOG</div>
                            <span className="font-mono text-[10px] text-zinc-600">REPLAY</span>
                        </div>
                        <div className="space-y-1.5 text-xs">
                            <div className="flex gap-2 text-zinc-300">
                                <span className="font-mono text-emerald-400 w-12">L27</span>
                                <span className="font-semibold">NOR overtakes VER</span>
                                <span className="text-zinc-500">— DRS + fresher tires (4-lap delta)</span>
                            </div>
                            <div className="flex gap-2 text-zinc-300">
                                <span className="font-mono text-amber-400 w-12">L26</span>
                                <span className="font-semibold">PIA pits</span>
                                <span className="text-zinc-500">— Soft → Hard, undercut attempt</span>
                            </div>
                            <div className="flex gap-2 text-zinc-300">
                                <span className="font-mono text-zinc-400 w-12">L25</span>
                                <span className="font-semibold">LEC sets purple S2</span>
                                <span className="text-zinc-500">— fresh hards, clean air</span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            {/* Floating glow */}
            <div className="absolute -inset-x-12 -inset-y-12 -z-10 bg-[radial-gradient(ellipse_50%_50%_at_50%_50%,rgba(225,6,0,0.1),transparent_70%)] pointer-events-none" />
        </div>
    );
}
